import { Badge } from "@/components/ui/Badge";
import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { Separator } from "@/components/ui/Separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import { displayValue } from "@/utils/Helper";
import {useGetTaskByIdQuery} from "@/services/TasksApi";
import { TaskPriority, TaskStatus } from "@/app/types/tasks";
import {useGetStaffListQuery} from "@/services/StaffsApi";
import { Eye } from "lucide-react";
import {cn} from "@/lib/utils";
import CommentSection from "./CommentSection";
import CustomCollapsible from "@/components/ui/collapsible-card/CollapsibleCard"
import TaskForm from "@/components/admin/tasks/component/modal/TaskForm";
import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Edit } from "lucide-react";
import DetailLogs from "@/components/admin/tickets/ticket-detail/AuditLog";

interface TicketDetails {
  isOpen: boolean;
  setOpen: any;
  ticketId: number | undefined;
  access:string
}

const TaskDetails = ({ isOpen, setOpen, ticketId,access }: TicketDetails) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [action, setAction] = useState<"add" | "edit">("edit");
  const [openLog, setOpenLog] = useState(false);
  const [ticketNumber, setTicketNumber] = useState<undefined | Number>();
  const { data, isLoading, error, refetch } = useGetTaskByIdQuery(
    ticketId as number, // or you could use an appropriate fallback value like 0 or handle the undefined case elsewhere
    {
      skip: !isOpen || ticketId === undefined, // skip if ticketId is undefined or isOpen is false
    }
  );
  const { data: staffData } = useGetStaffListQuery({ query: "" });

// const getFileName = (url: string) => {
//   return url.split("/").pop(); // Extracts the last part of the URL (file name)
// };

  const getFileName = (url: string) => {
    const lastPart = url.split("/").pop() || "";
    // Remove UUID prefix if present and convert to lowercase
    const fileName = lastPart.replace(/^[\w-]+-/, "").toLowerCase();
    return fileName;
  };

  const getFileType = (url: string) => {
    const extension = url.split(".").pop()?.toUpperCase(); // Extracts the file extension
    return extension ? extension : "Unknown";
  };

  const handleEditTask = () => {
    if (data) {
      setAction("edit");
      setIsDialogOpen(true);
    }
  };
  
   const handleOpenChange = (open: boolean) => {
     setOpen(open);
     if (open) {
       // Trigger refetch whenever the sheet opens
       refetch();
     }
   };
 
  if (isLoading && isOpen) {
    return (
      <div className="p-4">
        <Sheet >
          <SheetContent className="!h-screen !max-w-[900px] !max-h-[400px] overflow-auto fancy-scrollbar">
            <SheetHeader>
              <SheetTitle>Loading...</SheetTitle>
            </SheetHeader>
            <Separator className=" h-[1px] rounded-full my-2" />
            <Card className="w-full max-w-2xl mx-auto">
              <CardHeader className="space-y-1">
                <div className="flex items-center justify-between">
                  <div className="w-32 h-6 bg-gray-200 animate-pulse" />
                  <div className="w-16 h-6 bg-gray-200 animate-pulse" />
                </div>
                <div className="space-y-1">
                  <div className="flex items-baseline gap-2">
                    <div className="w-32 h-8 bg-gray-200 animate-pulse" />
                    <div className="w-16 h-6 bg-gray-200 animate-pulse" />
                  </div>
                  <div className="w-48 h-6 bg-gray-200 animate-pulse" />
                  <div className="w-32 h-6 bg-gray-200 animate-pulse" />
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-200 animate-pulse" />
                    <div className="space-y-1 w-48 h-6 bg-gray-200 animate-pulse" />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-200 animate-pulse" />
                    <div className="space-y-1 w-48 h-6 bg-gray-200 animate-pulse" />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-200 animate-pulse" />
                    <div className="space-y-1 w-48 h-6 bg-gray-200 animate-pulse" />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-200 animate-pulse" />
                    <div className="space-y-1 w-48 h-6 bg-gray-200 animate-pulse" />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-200 animate-pulse" />
                    <div className="space-y-1 w-48 h-6 bg-gray-200 animate-pulse" />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-200 animate-pulse" />
                    <div className="space-y-1 w-48 h-6 bg-gray-200 animate-pulse" />
                  </div>
                </div>
                <div className="pt-4 space-y-4">
                  <h3 className="font-semibold">Comments</h3>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-gray-200 animate-pulse rounded-full" />
                    <div className="space-y-1 w-48 h-6 bg-gray-200 animate-pulse" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  if (error) {
    return <div>Error loading Task details</div>;
  }

  // Trigger refetch when the sheet is opened
 
  return (
    <div>
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent className="!h-screen !max-w-[700px]  overflow-auto fancy-scroll">
          <SheetHeader>
            <SheetTitle className="flex items-center justify-between gap-2">
              Task
              {access === "write" && (
                <Button
                  onClick={handleEditTask}
                  className="flex items-center gap-2 h-8 w-fit m-3"
                >
                  <Edit className="h-4 w-4" />
                </Button>
              )}
            </SheetTitle>
          </SheetHeader>
          <Separator className=" h-[1px] rounded-full my-2" />

          <CustomCollapsible
            title="Details"
            hideToggle={true}
            isOpenDefault={true}
            className="border bg-secondary border-gray-300 shadow-md rounded-lg"
          >
            <div className="flex flex-col gap-2">
              <span className="capitalize text-sm md:text-base break-words whitespace-pre-line text-muted-foreground">
                <span className="text-black">Title</span> : {data?.title}
                {/* <Button> Edit Task </Button> */}
              </span>
              <span className="capitalize text-sm md:text-base break-words whitespace-pre-line text-muted-foreground">
                <span className="text-black">Description</span> :{" "}
                {data?.description.length ? data?.description : ""}
              </span>
              <div className="grid grid-cols-2 gap-2">
                <span className="capitalize text-sm md:text-base break-words whitespace-pre-line text-muted-foreground">
                  <span className="text-black">Due Date : </span>
                  {new Date(data?.due_date ?? "")
                    .toLocaleDateString("en-PK", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })
                    .replace(/\//g, "-")}
                </span>
                <span className="capitalize text-sm md:text-base break-words whitespace-pre-line text-muted-foreground">
                  <span className="text-black">Status : </span>
                  <Badge
                    className={cn(
                      data?.status === TaskStatus.TODO
                        ? "bg-[#3dc7d1]"
                        : data?.status === TaskStatus.IN_PROGRESS
                          ? "bg-[#63c82c]"
                          : data?.status === TaskStatus.HOLD
                            ? "bg-[#ff9429]"
                            : data?.status === TaskStatus.COMPLETED
                              ? "bg-[#4fcc41]"
                              : data?.status === TaskStatus.ARCHIVED
                                ? "bg-[#cf3040]"
                                : "",
                      "text-black px-3 py-1 w-fit rounded-full text-sm"
                    )}
                  >
                    {/* {data?.status
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase())} */}
                    {data?.status === TaskStatus.TODO
                      ? "PENDING"
                      : data?.status
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c) => c.toLocaleUpperCase())}
                  </Badge>
                </span>
                <span className="capitalize text-sm md:text-base break-words whitespace-pre-line text-muted-foreground">
                  <span className="text-black">Priority : </span>
                  <Badge
                    className={cn(
                      data?.priority === TaskPriority.LOW
                        ? "bg-[#4CAF50]"
                        : data?.priority === TaskPriority.MEDIUM
                          ? "bg-[#FFC107]"
                          : data?.priority === TaskPriority.HIGH
                            ? "bg-[#F44336]"
                            : "",
                      "text-black px-3 py-1 w-fit rounded-full text-sm"
                    )}
                  >
                    {displayValue(data?.priority ?? "")}
                  </Badge>
                </span>
                <span className="capitalize text-sm md:text-base break-words whitespace-pre-line text-muted-foreground items-center">
                  <span className="text-black"> Assigned To : </span>
                  <Badge className="bg-blue-400 text-black px-3 py-1 w-fit rounded-full text-sm">
                    {displayValue(
                      staffData?.find(
                        (staff) => staff.value === data?.assigned_to
                      )?.label || "N/A"
                    )}
                  </Badge>
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <span className="capitalize text-sm md:text-base break-words whitespace-pre-line text-muted-foreground">
                  <span className="text-black">Linked Ticket : </span>
                  <span
                    onClick={() => {
                      setTicketNumber(data?.linked_ticket?.ticket_id);
                      setOpenLog(true);
                    }}
                    className="underline cursor-pointer"
                  >
                    {data?.linked_ticket?.ticket_number}
                  </span>
                </span>
              </div>
            </div>
          </CustomCollapsible>
          <div className="pt-2 space-y-4">
            <CustomCollapsible
              title="Tags"
              hideToggle={true}
              isOpenDefault={true}
              className="border bg-secondary border-gray-300 shadow-md rounded-lg"
            >
              <div className="w-full gap-2 flex flex-row">
                {(data?.tags ?? []).length > 0 ? (
                  (data?.tags ?? []).map((tag, index) => (
                    <Badge
                      key={index}
                      className="bg-blue-500 text-black px-3 py-1 w-fit rounded-full text-sm"
                    >
                      {tag}
                    </Badge>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No Tags</p>
                )}
              </div>
            </CustomCollapsible>
          </div>
          <div className="pt-2 space-y-4">
            <CustomCollapsible
              title="Attachments"
              hideToggle={true}
              isOpenDefault={true}
              className="border bg-secondary border-gray-300 shadow-md rounded-lg"
            >
              <div className="w-full">
                {(data?.attachments ?? []).length > 0 ? (
                  <ul className="max-h-40 overflow-y-auto border rounded p-2 bg-gray-50">
                    {(data?.attachments ?? []).map((url, index) => (
                      <li
                        key={index}
                        className="flex justify-between items-center text-sm p-1 border-b last:border-none"
                      >
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline flex items-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          <span>
                            View File {getFileName(url)} ({getFileType(url)})
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">No Attachments</p>
                )}
              </div>
            </CustomCollapsible>
          </div>
          {/* Comment Section */}
          {ticketId && <CommentSection ticketId={ticketId} access={access} />}
        </SheetContent>
      </Sheet>
      <DetailLogs
        isOpen={openLog}
        setOpen={setOpenLog}
        ticketId={ticketNumber}
      />

      {data && (
        <TaskForm
          isOpen={isDialogOpen}
          setOpen={setIsDialogOpen}
          action={action}
          setAction={setAction}
          refetch={refetch}
          data={data}
          setData={() => {}}
          isgettingCreate={false}
        />
      )}
    </div>
  );
};
export default TaskDetails;
